import React, { useEffect, useRef, useState } from "react"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Tooltip,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import { getKeycloakUsers } from "helpers/backend_helper"
import {
  defaultPageSizeOptions,
  getLottieLoadingOptions,
  showToast,
  stringSorter,
  downloadExcel,
  exportPDF,
  getLottieSearch,
  dateSorter,
  dateFormatter,
} from "utils/general"
import OrganizationUserDrawer from "./OrganizationUserDrawer"
import TagsTree from "components/DropdownList/TagsTree"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import Lottie from "react-lottie"
import classnames from "classnames"
import "jspdf-autotable"
import { Table as AntdTable, Input as AntdInput } from "antd"
import { tableDefaultHeight } from "constants/layout"
import moment from "moment"
import QuickGuide from "components/QuickGuides/QuickGuide"
import SelectKeycloakGroup from "components/DropdownList/SelectKeycloakGroup"
const { Search } = AntdInput

const OrganizationUserManagement = props => {
  const [groups, setGroups] = useState([])
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [selectedRow, setSelectedRow] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [selectedUsers] = useState([])
  const [ttdownloadstatstable, setTtdownloadstatstable] = useState(false) // Tooltip state for download button
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [selectedTags, setSelectedTags] = useState([])
  const [title] = useState(props.t("User management"))
  const [colSize] = useState(12)
  const [quickGuide, setQuickGuide] = useState(false)

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  // Search or filter changed
  useEffect(() => {
    handleSearch()
  }, [searchText, users])

  // Name tags state update from app-users-selected.js
  useEffect(() => {
    if (selectedUsers.length == 0) {
      start()
    }
  }, [selectedUsers])

  // Handle search function
  const handleSearch = () => {
    let searchTextLowercase = searchText.toLowerCase()
    let result = users.filter(
      data =>
        data.username?.toLowerCase().includes(searchTextLowercase) ||
        data.description?.toLowerCase().includes(searchTextLowercase) ||
        data.groups?.toLowerCase().includes(searchTextLowercase) ||
        data.gsm?.toLowerCase().includes(searchTextLowercase) ||
        data.email?.toLowerCase().includes(searchTextLowercase) ||
        data.fullName?.toLowerCase().includes(searchTextLowercase)
    )
    setFilteredUsers(result)
  }

  // Get admin users for table
  const getAdmins = () => {
    setUsers([])
    if (!loading && groups?.length > 0) {
      setLoading(true)
      getKeycloakUsers(abortRef.current, groups, true, false)
        .then(result => {
          // console.log("userlist:", result)
          if (result.entries) {
            result.entries.sort((a, b) => (a.username > b.username ? 1 : -1))
            for (const user of result.entries) {
              let first = user.firstName ?? ""
              let last = user.lastName ?? ""
              user.fullName = (first + " " + last).trim()
              if (user.attributes) {
                user.description = user.attributes.description
                  ? user.attributes.description[0]
                  : ""
                user.gsm = user.attributes.gsm ? user.attributes.gsm[0] : ""
                user.videoRoomId = user.attributes.videoRoomId
                  ? user.attributes.videoRoomId[0]
                  : ""
                user.lastLogin = user.attributes.lastLogin
                  ? moment(user.attributes.lastLogin[0]).valueOf()
                  : 0
              }
            }
            setUsers(result.entries)
          } else {
            setUsers([])
          }
        })
        .catch(err => {
          console.log(err)
          setUsers([])
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Empty selected users
  const start = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 200)
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [
        props.t("Username"),
        props.t("Description"),
        props.t("Professional"),
        props.t("Email"),
        props.t("GSM"),
        props.t("Enabled"),
        props.t("Created"),
        props.t("Last login"),
        props.t("Groups"),
      ],
    ]
    const data = filteredUsers.map(elt => [
      elt.username,
      elt.description,
      elt.basicUser ? props.t("Yes") : props.t("No"),
      elt.email,
      elt.gsm,
      elt.enabled ? props.t("Yes") : props.t("No"),
      dateFormatter(elt.createdTimestamp),
      dateFormatter(elt.lastLogin),
      elt.groups,
    ])

    downloadExcel(title, heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  const generatePdf = () => {
    const heading = [
      props.t("Username"),
      props.t("Description"),
      props.t("Professional"),
      props.t("Email"),
      props.t("GSM"),
      props.t("Enabled"),
      props.t("Created"),
      props.t("Last login"),
      props.t("Groups"),
    ]
    const data = filteredUsers.map(elt => [
      elt.username,
      elt.description,
      elt.basicUser ? props.t("Yes") : props.t("No"),
      elt.email,
      elt.gsm,
      elt.enabled ? props.t("Yes") : props.t("No"),
      dateFormatter(elt.createdTimestamp),
      dateFormatter(elt.lastLogin),
      elt.groups,
    ])

    const styles = {
      styles: { overflow: "linebreak" },
      columnStyles: {
        7: { cellWidth: 80 },
      },
    }

    exportPDF(title, heading, data, null, styles)
    showToast(props.t("PDF file exported succesfully!"), "success")
  }

  // Table structure
  const columns = [
    {
      title: props.t("Actions"),
      dataIndex: "actions",
      align: "center",
      render: editFormatter,
      fixed: "left",
      width: "100px",
      ellipsis: true,
    },
    {
      title: props.t("Username"),
      dataIndex: "username",
      render: usernameFormatter,
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "username", sortOrder)
      },
      fixed: "left",
      width: "200px",
      ellipsis: true,
    },
    {
      title: props.t("Name"),
      dataIndex: "fullName",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "fullName", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      title: props.t("Description"),
      dataIndex: "description",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "description", sortOrder)
      },
      width: "250px",
      ellipsis: true,
    },
    {
      title: props.t("Professional"),
      dataIndex: "basicUser",
      sorter: (a, b) => a.basicUser - b.basicUser,
      render: basicFormatter,
      align: "center",
      width: "150px",
      ellipsis: true,
    },
    {
      title: props.t("Email"),
      dataIndex: "email",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "email", sortOrder)
      },
      width: "250px",
      ellipsis: true,
    },
    {
      title: props.t("GSM"),
      dataIndex: "gsm",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "gsm", sortOrder)
      },
      width: "150px",
      ellipsis: true,
    },
    {
      title: props.t("Created"),
      dataIndex: "createdTimestamp",
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["createdTimestamp"], b["createdTimestamp"], sortOrder)
      },
      render: dateFormatter,
      width: "190px",
      ellipsis: true,
    },
    {
      title: props.t("Last login"),
      dataIndex: "lastLogin",
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["lastLogin"], b["lastLogin"], sortOrder)
      },
      render: dateFormatter,
      width: "190px",
      ellipsis: true,
    },
    {
      title: props.t("Groups"),
      dataIndex: "groups",
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "groups", sortOrder)
      },
      width: "150px",
      ellipsis: true,
    },
  ]

  // Table formatting functions start
  function editFormatter(cell, row) {
    return (
      <>
        <Button
          color="primary"
          outline
          size="sm"
          onClick={() => {
            setShowDrawer(true)
            setSelectedRow(row)
          }}
        >
          <i className="mdi mdi-pencil" />
        </Button>
      </>
    )
  }

  function usernameFormatter(cell, row) {
    return (
      <>
        <span className={classnames({ "text-danger": !row.enabled })}>
          {row.username}
        </span>
        {!row.enabled && (
          <span className="ms-2 badge bg-danger font-size-12">
            {props.t("Disabled")}
          </span>
        )}
        {row.videoRoomId && !!row.groupHost && (
          <i className="ms-2 mdi mdi-account-voice text-primary" />
        )}
      </>
    )
  }

  function basicFormatter(cell, row) {
    if (row.basicUser) {
      return <i className="mdi mdi-check-bold text-primary"></i>
    } else return <>{/* <i className="mdi mdi-close text-light"></i> */}</>
  }

  return (
    <>
      <div className="page-content">
        <Container fluid className={quickGuide ? "quick-guide-open" : ""}>
          <Row className="align-items-baseline">
            <Col xs="6" className="d-flex align-items-baseline">
              <Breadcrumbs title={title} />
            </Col>
            <Col
              xs="6"
              className="d-flex align-items-baseline justify-content-end"
            >
              <QuickGuide
                source="userManagement"
                title="User management"
                isOpen={quickGuide}
                setIsOpen={setQuickGuide}
              />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col xs="4">
              <div className="d-inline-block" style={{ width: "100%" }}>
                <TagsTree
                  loadData={true}
                  expanded={true}
                  selectionChanged={setSelectedTags}
                />
              </div>
            </Col>
            <Col xs="4">
              <SelectKeycloakGroup
                disabled={loading}
                selection={groups}
                onChange={setGroups}
                multiple={true}
                selectedTags={selectedTags}
              />
            </Col>
            <Col className="col-auto">
              <Button
                disabled={loading}
                color="primary"
                style={{
                  minWidth: "80px",
                  height: "32px",
                  padding: "inherit",
                }}
                onClick={() => {
                  getAdmins()
                }}
              >
                {props.t("Search")}
              </Button>
            </Col>
          </Row>

          {!loading && users?.length == 0 && (
            <Card>
              <CardBody>
                <div className="text-center my-5">
                  <Lottie
                    options={getLottieSearch()}
                    height={150}
                    width={180}
                  ></Lottie>
                </div>
              </CardBody>
            </Card>
          )}

          {loading && (
            <Card>
              <CardBody>
                <div className="text-center my-5">
                  <Lottie
                    options={getLottieLoadingOptions()}
                    height={100}
                    width={100}
                  ></Lottie>
                </div>
              </CardBody>
            </Card>
          )}

          {users?.length > 0 && !loading && (
            <div>
              <Row>
                <Col sm={colSize}>
                  <Card>
                    <CardBody>
                      <Row>
                        <Col
                          className="mb-3"
                          style={{ maxWidth: "320px" }}
                        >
                          <Search
                            placeholder={props.t("Search table")}
                            allowClear
                            value={searchText}
                            onChange={event => {
                              setSearchText(event.target.value)
                            }}
                            style={{ minWidth: "240px" }}
                          />
                        </Col>
                        <Col>
                          <span className="float-end ms-2">
                            <Tooltip
                              placement="bottom"
                              isOpen={ttdownloadstatstable}
                              target="downloadstatstable"
                              toggle={() => {
                                setTtdownloadstatstable(
                                  !ttdownloadstatstable
                                )
                              }}
                            >
                              {props.t("Download statistics")}
                            </Tooltip>
                            <Dropdown
                              id="printButtonServicesStats"
                              isOpen={menu}
                              toggle={() => setMenu(!menu)}
                              className="download-btn text-center"
                              type="button"
                            >
                              <DropdownToggle
                                id="downloadstatstable"
                                className="text-muted"
                                tag="i"
                              >
                                <i className="mdi mdi-download" />
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem
                                  onClick={() => generateExcel()}
                                >
                                  {props.t("Download .xlsx")}
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => generatePdf()}
                                >
                                  {props.t("Download .pdf")}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </span>
                          <span className="mt-2 float-end">
                            <p>
                              {filteredUsers.length} {props.t("users")}
                            </p>
                          </span>
                        </Col>
                      </Row>
                      <div className="mt-2">
                        <AntdTable
                          className="striped colored-header narrow-header"
                          bordered
                          size="small"
                          rowKey={"id"}
                          columns={columns}
                          dataSource={filteredUsers}
                          pagination={{
                            showSizeChanger: true,
                            defaultPageSize: 100,
                            pageSizeOptions: defaultPageSizeOptions,
                          }}
                          tableLayout="auto"
                          scroll={{
                            x: "max-content",
                            y: tableDefaultHeight,
                          }}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          <OrganizationUserDrawer
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            selectedRow={selectedRow}
            getAdmins={getAdmins}
          />
        </Container>
      </div>
    </>
  )
}

OrganizationUserManagement.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(OrganizationUserManagement)
